/**
 * A number range's answer is its two ends joined by `-`, as `"from-to"`. spdata-api and its export
 * store and write that string without parsing it, so the encoding stays. A bound can be negative, so
 * the answer is read with one pattern that allows a sign on each bound, never with `split('-')`
 * (ENG-9046).
 */

export interface RangeEnds {
  from: number | undefined;
  to: number | undefined;
}

/**
 * One bound: what `${number}` writes (a sign, a decimal, an exponent for very small or large values),
 * plus the `+`, `.5` and `2.` that `Number` accepted when the old reader split the answer.
 */
const BOUND = String.raw`[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?`;

/**
 * "from-to", with the spaces around the dash the old reader tolerated. A "from" with no "to" after
 * it, as in `5` or `5-`, reads as "from" alone: the old reader made `5-` a "to" of 0, an answer
 * nobody gave. Anything else reads as neither end.
 */
const RANGE_ANSWER = new RegExp(String.raw`^(${BOUND})(?:\s*-\s*(${BOUND})?)?$`, 'i');

export const readRangeAnswer = (answer: string | undefined): RangeEnds => {
  const match = answer ? RANGE_ANSWER.exec(answer.trim()) : null;
  if (!match) {
    return { from: undefined, to: undefined };
  }
  return {
    from: Number(match[1]),
    to: match[2] === undefined ? undefined : Number(match[2])
  };
};

/** A range with one end missing is unanswered, and sends `''` (ADR 0009). */
export const writeRangeAnswer = (from: number | null | undefined, to: number | null | undefined): string =>
  from == null || to == null ? '' : `${from}-${to}`;

/** Where a range is drawn in its field: a matrix cell's row and column ids, or two empty strings for the field itself. */
export interface RangePlace {
  row: string;
  column: string;
}

/**
 * The ends each range last held, by the field it is drawn for and its place in it, so a half-filled range, which saves
 * `''`, still shows its typed end after the virtual list remounts it. Keyed weakly on the store's field object, so
 * opening another entry, which loads new field objects, leaves nothing behind.
 */
const keptEnds = new WeakMap<object, Map<string, Map<string, RangeEnds>>>();

export const keepRangeEnds = (field: object, { row, column }: RangePlace, ends: RangeEnds) => {
  const rows = keptEnds.get(field) ?? new Map<string, Map<string, RangeEnds>>();
  const columns = rows.get(row) ?? new Map<string, RangeEnds>();
  columns.set(column, { ...ends });
  rows.set(row, columns);
  keptEnds.set(field, rows);
};

/**
 * The ends to draw for `answer`: the ones last kept, while they still write that answer, or else the answer's own. An
 * answer something else wrote since, such as a clear or an Assign, wins over what was kept.
 */
export const rangeEndsFor = (field: object, { row, column }: RangePlace, answer: string | undefined): RangeEnds => {
  const kept = keptEnds.get(field)?.get(row)?.get(column);
  return kept && writeRangeAnswer(kept.from, kept.to) === (answer ?? '') ? { ...kept } : readRangeAnswer(answer);
};

/**
 * Forgets what a matrix row's ranges held, for the matrix to call as it removes the row: a row added after it can take
 * the same id, and would otherwise draw the removed row's half-filled ends. Any other way rows go, such as opening
 * another entry, loads a new field object and so leaves nothing kept.
 */
export const forgetRowRangeEnds = (field: object, row: string) => {
  keptEnds.get(field)?.delete(row);
};

/**
 * Forgets every range the field holds, for the Show effect to call as it hides and clears the field: a half-filled
 * range's answer is `''` before the clear and after it, so the item can't see the clear in its answer.
 */
export const forgetRangeEnds = (field: object) => {
  keptEnds.delete(field);
};
